import React from 'react';
import Slider from 'react-slick';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const CarouselContainer = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 90vh;
  overflow: hidden;
  
  @media (max-width: 768px) {
    height: 70vh;
  }
`;

const SlideItem = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 90vh;
  background-image: url(${props => props.$bgImage});
  background-size: cover;
  background-position: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.4) 0%,
      rgba(0, 0, 0, 0.6) 100%
    );
    backdrop-filter: blur(2px);
    z-index: 1;
  }
  
  @media (max-width: 768px) {
    height: 70vh;
  }
`;

const SlideContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  text-align: center;
  width: 80%;
  max-width: 1000px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const SlideTitle = styled(motion.h1)`
  font-size: 3.5rem;
  color: #FFFFFF;
  margin-bottom: 20px;
  font-weight: 700;
  text-shadow: 2px 4px 8px rgba(0, 0, 0, 0.3);
  
  span {
    color: #D4AF37;
    text-shadow: 2px 4px 12px rgba(212, 175, 55, 0.4);
  }
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const SlideDescription = styled(motion.p)`
  font-size: 1.2rem;
  color: #F0F0F0;
  text-shadow: 1px 2px 4px rgba(0, 0, 0, 0.3);
  margin-bottom: 20px;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 15px;
  }
`;

const SlideButtonContainer = styled.div`
  position: relative;
  margin-top: 25px;
  z-index: 5;
`;

const SlideButtonEffect = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #000;
  border-radius: 8px;
  transform: rotate(-5deg);
  z-index: -1;
  transition: all 0.3s ease;
`;

const SlideButton = styled(Link)`
  display: inline-block;
  background-color: #ffffff;
  color: #121212;
  padding: 15px 40px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 1rem;
  position: relative;
  overflow: hidden;
  z-index: 1;
  letter-spacing: 1px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: var(--primary-color);
    transition: all 0.4s cubic-bezier(0.68, -0.6, 0.32, 1.6);
    z-index: -1;
  }
  
  &:hover {
    color: #ffffff;
    transform: scale(1.05);
    
    &::before {
      left: 0;
    }
    
    & + ${SlideButtonEffect} {
      transform: rotate(0deg) scale(1.1);
      background: var(--secondary-color);
    }
  }
  
  @media (max-width: 768px) {
    padding: 12px 30px;
    font-size: 0.9rem;
  }
`;

const ArrowContainer = styled.div`
  position: absolute;
  bottom: 30px;
  right: 30px;
  display: flex;
  gap: 15px;
  z-index: 20;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const ArrowButton = styled.button`
  width: 60px;
  height: 60px;
  background-color: rgba(255, 255, 255, 0.9);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--primary-color);
    transform: translateY(100%);
    transition: transform 0.3s ease;
    z-index: 1;
  }
  
  svg {
    font-size: 24px;
    color: #121212;
    position: relative;
    z-index: 2;
    transition: all 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-5px);
    
    &::before {
      transform: translateY(0);
    }
    
    svg {
      color: white;
    }
  }
  
  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    
    svg {
      font-size: 18px;
    }
  }
`;

const PrevArrow = styled(ArrowButton)`
  clip-path: polygon(0 0, 100% 0, 85% 100%, 0 100%);
`;

const NextArrow = styled(ArrowButton)`
  clip-path: polygon(0 0, 100% 0, 100% 100%, 15% 100%);
`;

const carouselData = [
  {
    id: 1,
    image: "https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1951&q=80",
    title: "Istri Wala - Premium Ironing & Laundry Solutions",
    description: "Professional steam ironing, dry cleaning, laundry, and shoe cleaning services with free pickup and delivery within 5km.",
    buttonText: "Our Services",
    buttonLink: "/services"
  },
  {
    id: 2,
    image: "https://images.unsplash.com/photo-1489274495757-95c7c837b101?ixlib=rb-4.0.3&auto=format&fit=crop&w=1951&q=80",
    title: "Expert Dry Cleaning & Laundry",
    description: "Professional dry cleaning for delicate fabrics and complete wash & fold laundry services with premium care.",
    buttonText: "Learn More",
    buttonLink: "/services"
  },
  {
    id: 3,
    image: "https://images.unsplash.com/photo-1517677208171-0bc6725a3e60?ixlib=rb-4.0.3&auto=format&fit=crop&w=1950&q=80",
    title: "Flexible Pricing Plans",
    description: "Choose from affordable membership plans or pay-per-use pricing. Member and non-member options available.",
    buttonText: "View Pricing",
    buttonLink: "/pricing" 
  },
  {
    id: 4,
    image: "https://images.unsplash.com/photo-1521656693074-0ef32e80a5d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1950&q=80",
    title: "Free Pickup & Delivery",
    description: "Enjoy complimentary pickup and delivery services within 5km. Convenient scheduling at your preferred time.",
    buttonText: "Contact Us",
    buttonLink: "/contact"
  }
];

function NextArrowComponent(props) {
  const { className, style, onClick } = props;
  return (
    <NextArrow
      className={className}
      style={{ ...style }}
      onClick={onClick}
      aria-label="Next slide"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 18l6-6-6-6"/>
      </svg>
    </NextArrow>
  );
}

function PrevArrowComponent(props) {
  const { className, style, onClick } = props;
  return (
    <PrevArrow
      className={className}
      style={{ ...style }}
      onClick={onClick}
      aria-label="Previous slide"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 18l-6-6 6-6"/>
      </svg>
    </PrevArrow>
  );
}

const HomeCarousel = () => {
  const settings = {
    dots: false,
    infinite: true,
    speed: 800,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: false,
    cssEase: "cubic-bezier(0.45, 0, 0.55, 1)",
    arrows: false,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          autoplaySpeed: 3500
        }
      }
    ]
  };
  
  const sliderRef = React.useRef(null);

  const goToNext = () => {
    sliderRef.current.slickNext();
  };

  const goToPrev = () => {
    sliderRef.current.slickPrev();
  };

  return (
    <CarouselContainer
      initial={{ opacity: 0, scale: 1.1 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.2, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      <Slider ref={sliderRef} {...settings}>
        {carouselData.map((slide) => (
          <SlideItem key={slide.id} $bgImage={slide.image}>
            <SlideContent>
              <SlideTitle
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                {slide.title.split(' ').map((word, index) => 
                  index % 3 === 0 ? <span key={index}>{word} </span> : `${word} `
                )}
              </SlideTitle>
              <SlideDescription
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                {slide.description}
              </SlideDescription>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.6, 
                  delay: 0.5,
                  type: "spring",
                  stiffness: 120
                }}
              >
                <SlideButtonContainer>
                  <SlideButton to={slide.buttonLink}>{slide.buttonText}</SlideButton>
                  <SlideButtonEffect />
                </SlideButtonContainer>
              </motion.div>
            </SlideContent>
          </SlideItem>
        ))}
      </Slider>
      <ArrowContainer>
        <PrevArrowComponent onClick={goToPrev} />
        <NextArrowComponent onClick={goToNext} />
      </ArrowContainer>
    </CarouselContainer>
  );
};

export default HomeCarousel;
